import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Stack,
  Alert,
  Typography,
  Paper,
  Collapse,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FlightIcon from '@mui/icons-material/Flight';
import SnoozeIcon from '@mui/icons-material/Snooze';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useOperationsInboxData } from './hooks/useOperationsInboxData';
import { useLocationUsageCounts } from './hooks/useLocationUsageCounts';
import { useInboxPolling } from './hooks/useInboxPolling';
import { useApi } from '../../containers/ApiContainer';
import { useActiveFBOs } from '../../containers/ActiveFBOContainer';
import { useHangarsState, HangarsStateContainer } from '../../containers/HangarsStateContainer';
import { useRampsState, RampsStateContainer } from '../../containers/RampsStateContainer';
import { useSnackbar } from '../../containers/SnackbarContainer';
import { InboxService } from '../../services/InboxService';
import { InboxHeader } from './components/InboxHeader';
import { ActionTypeGroup } from './components/ActionTypeGroup';
import { QuarantineCard } from './components/QuarantineCard';
import { TripCard } from './components/TripCard';
import { BulkActionBar } from './components/BulkActionBar';
import { InboxFilters, InboxItem, getUrgencyLevel, matchesTimeFilter } from './types';

/**
 * Main Operations Inbox view
 * Combines quarantine items (unknown aircraft) and unassigned trips in one place
 */
function OperationsInboxViewInner() {
  const { t } = useTranslation();
  const { postgrest } = useApi();
  const { activeFBO } = useActiveFBOs();
  const { hangars = [] } = useHangarsState();
  const { ramps = [] } = useRampsState();
  const snackbar = useSnackbar();
  const { hangarCounts, rampCounts } = useLocationUsageCounts();

  // Combine hangar and ramp counts into single map for TripCard
  const locationUsageCounts = useMemo(() => {
    const combined = new Map<string, number>();
    hangarCounts.forEach((count, id) => combined.set(id, count));
    rampCounts.forEach((count, id) => combined.set(id, count));
    return combined;
  }, [hangarCounts, rampCounts]);

  const [filters, setFilters] = useState<InboxFilters>({
    search: '',
    showSnoozed: false,
    timeFilter: 'today', // Default to today - show only what matters now
  });

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [snoozedExpanded, setSnoozedExpanded] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date>(new Date());
  const isMountedRef = useRef(true);

  const { items, setItems, groups, totalCount, actionableCount, isLoading, error, refetch } =
    useOperationsInboxData({ ...filters, showSnoozed: true });

  // Track mounted state to prevent state updates after unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  // Track when data was last refreshed
  const handleRefresh = useCallback(() => {
    if (!isMountedRef.current) return;

    refetch();

    // Defer state update to ensure component is still mounted
    setTimeout(() => {
      if (isMountedRef.current) {
        setLastRefreshTime(new Date());
      }
    }, 0);
  }, [refetch]);

  // Auto-refresh every 60 seconds when tab is active
  useInboxPolling({
    enabled: true,
    intervalMs: 60000,
    onRefresh: handleRefresh,
  });

  // Update browser tab title with count
  useEffect(() => {
    const urgentCount = items.filter(i => {
      if (i.item_type !== 'unassigned_trip') return false;
      const urgency = getUrgencyLevel(i.arrival_date);
      return urgency === 'critical' || urgency === 'urgent';
    }).length;

    if (urgentCount > 0) {
      document.title = t("screens.operationsInbox.tabTitles.urgent", { count: actionableCount });
    } else if (actionableCount > 0) {
      document.title = t("screens.operationsInbox.tabTitles.actionable", { count: actionableCount });
    } else {
      document.title = t("screens.operationsInbox.tabTitles.default");
    }

    return () => { document.title = t("screens.operationsInbox.tabTitles.appDefault"); };
  }, [actionableCount, items, t]);

  // Get selected items for bulk action bar
  const selectedItems = useMemo(() => {
    return items.filter(item => selectedIds.has(item.item_id));
  }, [items, selectedIds]);

  // Filter items based on time filter
  const filteredGroups = useMemo(() => {
    // For 'all' view, show both sections. For time-specific views, only show items matching that time.
    if (filters.timeFilter === 'all') {
      return groups;
    }
    // For today/tomorrow/week, filter both quarantine items and trips by arrival date
    // Quarantine items with dates will appear in appropriate time buckets
    // Quarantine items without dates will only appear in 'all' view
    return {
      unknownAircraft: groups.unknownAircraft.filter(item => matchesTimeFilter(item, filters.timeFilter)),
      needsRouting: groups.needsRouting.filter(item => matchesTimeFilter(item, filters.timeFilter)),
      snoozed: groups.snoozed,
    };
  }, [groups, filters.timeFilter]);

  // Calculate urgency counts for group header chips
  const urgencyCounts = useMemo(() => {
    const critical = filteredGroups.needsRouting.filter(i => getUrgencyLevel(i.arrival_date) === 'critical').length;
    const urgent = filteredGroups.needsRouting.filter(i => getUrgencyLevel(i.arrival_date) === 'urgent').length;
    const soon = filteredGroups.needsRouting.filter(i => getUrgencyLevel(i.arrival_date) === 'soon').length;
    return { critical, urgent, soon };
  }, [filteredGroups.needsRouting]);

  // Calculate count for current time filter
  const filteredCount = useMemo(() => {
    return filteredGroups.unknownAircraft.length + filteredGroups.needsRouting.length;
  }, [filteredGroups]);

  // Toggle selection
  const toggleSelection = useCallback((itemId: string) => {
    setSelectedIds(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  }, []);

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  // Handle resolving unknown aircraft
  const handleResolveAircraft = async (item: InboxItem, aircraftId: string, aircraftName: string) => {
    if (!postgrest || !activeFBO) return;

    setProcessingIds(prev => new Set(prev).add(item.item_id));

    try {
      await InboxService.resolveAircraft(postgrest, item.item_id, aircraftId);
      setItems(prev => prev.filter(i => i.item_id !== item.item_id));
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.item_id);
        return newSet;
      });

      snackbar.notify({
        text: t("screens.operationsInbox.messages.mapped", { tailNumber: item.tail_number, aircraftName }),
        severity: 'success',
      });
    } catch (err) {
      snackbar.notify({
        text: t("screens.operationsInbox.errors.resolveFailed", { tailNumber: item.tail_number, error: err instanceof Error ? err.message : t("screens.operationsInbox.errors.unknownError") }),
        severity: 'error',
      });
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.item_id);
        return newSet;
      });
    }
  };

  // Bulk resolve quarantine items
  const handleBulkResolve = async (aircraftId: string, aircraftName: string) => {
    if (!postgrest || !activeFBO) return;

    const quarantineItems = selectedItems.filter(i => i.item_type === 'quarantine');
    if (quarantineItems.length === 0) return;

    setBulkProcessing(true);

    try {
      const results = await InboxService.bulkResolveAircraft(
        postgrest,
        quarantineItems.map(i => i.item_id),
        aircraftId
      );

      const successCount = results.filter(r => r.success).length;
      const failCount = results.filter(r => !r.success).length;

      // Remove successful items from list
      const successIds = new Set(results.filter(r => r.success).map(r => r.pending_tenant_id));
      setItems(prev => prev.filter(i => !successIds.has(i.item_id)));
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        successIds.forEach(id => newSet.delete(id));
        return newSet;
      });

      if (failCount === 0) {
        snackbar.notify({
          text: t("screens.operationsInbox.messages.bulkMapped", { count: successCount, aircraftName }),
          severity: 'success',
        });
      } else {
        snackbar.notify({
          text: t("screens.operationsInbox.messages.bulkMappedPartial", { successCount, totalCount: quarantineItems.length, failCount }),
          severity: 'warning',
        });
      }
    } catch (err) {
      snackbar.notify({
        text: t("screens.operationsInbox.errors.bulkResolveFailed", { error: err instanceof Error ? err.message : t("screens.operationsInbox.errors.unknownError") }),
        severity: 'error',
      });
    } finally {
      setBulkProcessing(false);
    }
  };

  // Handle assigning trip to hangar
  const handleAssignToHangar = async (item: InboxItem, hangarId: string, hangarName: string) => {
    if (!postgrest || !item.trip_id || !item.arrival_date || !item.departure_date) return;

    setProcessingIds(prev => new Set(prev).add(item.item_id));

    try {
      await InboxService.assignToHangar(
        postgrest,
        item.trip_id,
        hangarId,
        item.arrival_date,
        item.departure_date
      );

      setItems(prev => prev.filter(i => i.item_id !== item.item_id));
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.item_id);
        return newSet;
      });

      snackbar.notify({
        text: t("screens.operationsInbox.messages.assigned", { tailNumber: item.tail_number, locationName: hangarName }),
        severity: 'success',
      });
    } catch (err) {
      snackbar.notify({
        text: t("screens.operationsInbox.errors.assignFailed", { error: err instanceof Error ? err.message : t("screens.operationsInbox.errors.unknownError") }),
        severity: 'error',
      });
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.item_id);
        return newSet;
      });
    }
  };

  // Bulk assign to hangar
  const handleBulkAssignHangar = async (hangarId: string, hangarName: string) => {
    if (!postgrest) return;

    const tripItems = selectedItems.filter(i => i.item_type === 'unassigned_trip' && i.trip_id);
    if (tripItems.length === 0) return;

    setBulkProcessing(true);
    let successCount = 0;
    const successfulItemIds = new Set<string>();
    const failedItems: { tailNumber: string; error: string }[] = [];

    for (const item of tripItems) {
      if (!item.trip_id || !item.arrival_date || !item.departure_date) continue;
      try {
        await InboxService.assignToHangar(
          postgrest,
          item.trip_id,
          hangarId,
          item.arrival_date,
          item.departure_date
        );
        successCount++;
        successfulItemIds.add(item.item_id);
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        failedItems.push({
          tailNumber: item.tail_number,
          error: errorMsg
        });
        console.error(`Failed to assign trip ${item.trip_id} to hangar:`, error);
      }
    }

    // Remove only successfully assigned items
    setItems(prev => prev.filter(i => !successfulItemIds.has(i.item_id)));
    setSelectedIds(new Set());
    setBulkProcessing(false);

    // Enhanced snackbar message with failure details
    const totalCount = tripItems.length;
    if (failedItems.length === 0) {
      snackbar.notify({
        text: t("screens.operationsInbox.messages.bulkAssignedHangar", { count: successCount, locationName: hangarName }),
        severity: 'success',
      });
    } else if (successCount === 0) {
      // Complete failure
      const sampleFails = failedItems.slice(0, 3).map(f => f.tailNumber).join(', ');
      const moreFails = failedItems.length > 3 ? ` +${failedItems.length - 3} more` : '';
      snackbar.notify({
        text: t("screens.operationsInbox.messages.bulkAssignedHangarFailed", { sampleFails, moreFails }),
        severity: 'error',
      });
    } else {
      // Partial success
      const sampleFails = failedItems.slice(0, 2).map(f => f.tailNumber).join(', ');
      const moreFails = failedItems.length > 2 ? '...' : '';
      snackbar.notify({
        text: t("screens.operationsInbox.messages.bulkAssignedHangarPartial", { successCount, totalCount, sampleFails, moreFails }),
        severity: 'warning',
      });
    }
  };

  // Handle assigning trip to ramp
  const handleAssignToRamp = async (item: InboxItem, rampId: string, rampName: string) => {
    if (!postgrest || !item.trip_id || !item.arrival_date || !item.departure_date) return;

    setProcessingIds(prev => new Set(prev).add(item.item_id));

    try {
      await InboxService.assignToRamp(
        postgrest,
        item.trip_id,
        rampId,
        item.arrival_date,
        item.departure_date,
        0, 0, 0
      );

      setItems(prev => prev.filter(i => i.item_id !== item.item_id));
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.item_id);
        return newSet;
      });

      snackbar.notify({
        text: t("screens.operationsInbox.messages.assigned", { tailNumber: item.tail_number, locationName: rampName }),
        severity: 'success',
      });
    } catch (err) {
      snackbar.notify({
        text: t("screens.operationsInbox.errors.assignFailed", { error: err instanceof Error ? err.message : t("screens.operationsInbox.errors.unknownError") }),
        severity: 'error',
      });
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.item_id);
        return newSet;
      });
    }
  };

  // Bulk assign to ramp
  const handleBulkAssignRamp = async (rampId: string, rampName: string) => {
    if (!postgrest) return;

    const tripItems = selectedItems.filter(i => i.item_type === 'unassigned_trip' && i.trip_id);
    if (tripItems.length === 0) return;

    setBulkProcessing(true);
    let successCount = 0;
    const successfulItemIds = new Set<string>();
    const failedItems: { tailNumber: string; error: string }[] = [];

    for (const item of tripItems) {
      if (!item.trip_id || !item.arrival_date || !item.departure_date) continue;
      try {
        await InboxService.assignToRamp(
          postgrest,
          item.trip_id,
          rampId,
          item.arrival_date,
          item.departure_date,
          0, 0, 0
        );
        successCount++;
        successfulItemIds.add(item.item_id);
      } catch (error) {
        const errorMsg = error instanceof Error ? error.message : 'Unknown error';
        failedItems.push({
          tailNumber: item.tail_number,
          error: errorMsg
        });
        console.error(`Failed to assign trip ${item.trip_id} to ramp:`, error);
      }
    }

    // Remove only successfully assigned items
    setItems(prev => prev.filter(i => !successfulItemIds.has(i.item_id)));
    setSelectedIds(new Set());
    setBulkProcessing(false);

    // Enhanced snackbar message with failure details
    const totalCount = tripItems.length;
    if (failedItems.length === 0) {
      snackbar.notify({
        text: t("screens.operationsInbox.messages.bulkAssignedRamp", { count: successCount, locationName: rampName }),
        severity: 'success',
      });
    } else if (successCount === 0) {
      // Complete failure
      const sampleFails = failedItems.slice(0, 3).map(f => f.tailNumber).join(', ');
      const moreFails = failedItems.length > 3 ? ` +${failedItems.length - 3} more` : '';
      snackbar.notify({
        text: t("screens.operationsInbox.messages.bulkAssignedRampFailed", { sampleFails, moreFails }),
        severity: 'error',
      });
    } else {
      // Partial success
      const sampleFails = failedItems.slice(0, 2).map(f => f.tailNumber).join(', ');
      const moreFails = failedItems.length > 2 ? '...' : '';
      snackbar.notify({
        text: t("screens.operationsInbox.messages.bulkAssignedRampPartial", { successCount, totalCount, sampleFails, moreFails }),
        severity: 'warning',
      });
    }
  };

  // Handle snoozing item
  const handleSnooze = async (item: InboxItem, until: Date) => {
    if (!postgrest || !activeFBO) return;

    try {
      await InboxService.snoozeItem(postgrest, {
        pendingTenantId: item.item_type === 'quarantine' ? item.item_id : undefined,
        tripId: item.item_type === 'unassigned_trip' ? item.trip_id || undefined : undefined,
        fboId: activeFBO.id,
        snoozeUntil: until,
      });

      setItems(prev => prev.filter(i => i.item_id !== item.item_id));
      setSelectedIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(item.item_id);
        return newSet;
      });

      snackbar.notify({
        text: t("screens.operationsInbox.messages.snoozed", { tailNumber: item.tail_number }),
        severity: 'success',
      });

      refetch();
    } catch (err) {
      snackbar.notify({
        text: t("screens.operationsInbox.errors.snoozeFailed", { error: err instanceof Error ? err.message : t("screens.operationsInbox.errors.unknownError") }),
        severity: 'error',
      });
    }
  };

  // Bulk snooze
  const handleBulkSnooze = async (until: Date) => {
    if (!postgrest || !activeFBO) return;

    setBulkProcessing(true);
    let successCount = 0;

    for (const item of selectedItems) {
      try {
        await InboxService.snoozeItem(postgrest, {
          pendingTenantId: item.item_type === 'quarantine' ? item.item_id : undefined,
          tripId: item.item_type === 'unassigned_trip' ? item.trip_id || undefined : undefined,
          fboId: activeFBO.id,
          snoozeUntil: until,
        });
        successCount++;
      } catch {
        // Continue with others
      }
    }

    setItems(prev => prev.filter(i => !selectedIds.has(i.item_id)));
    setSelectedIds(new Set());
    setBulkProcessing(false);

    snackbar.notify({
      text: t("screens.operationsInbox.messages.bulkSnoozed", { count: successCount }),
      severity: successCount === selectedItems.length ? 'success' : 'warning',
    });

    refetch();
  };

  // Handle unsnoozing item
  const handleUnsnooze = async (item: InboxItem) => {
    if (!postgrest || !activeFBO) return;

    try {
      await InboxService.unsnoozeItem(postgrest, {
        pendingTenantId: item.item_type === 'quarantine' ? item.item_id : undefined,
        tripId: item.item_type === 'unassigned_trip' ? item.trip_id || undefined : undefined,
        fboId: activeFBO.id,
      });

      snackbar.notify({
        text: t("screens.operationsInbox.messages.unsnoozed", { tailNumber: item.tail_number }),
        severity: 'success',
      });

      refetch();
    } catch (err) {
      snackbar.notify({
        text: t("screens.operationsInbox.errors.unsnoozeFailed", { error: err instanceof Error ? err.message : t("screens.operationsInbox.errors.unknownError") }),
        severity: 'error',
      });
    }
  };

  // Loading skeleton
  if (isLoading && items.length === 0) {
    return (
      <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3 }}>
        <Typography variant="h5" fontWeight={600} sx={{ mb: 3 }}>
          {t("screens.operationsInbox.title")}
        </Typography>
        <Stack spacing={1.5}>
          {[1, 2, 3, 4, 5].map(i => (
            <Box
              key={i}
              sx={{
                height: 48,
                bgcolor: 'rgba(0,0,0,0.03)',
                borderRadius: 1,
                borderLeft: '3px solid rgba(0,0,0,0.1)',
              }}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  if (error) {
    return (
      <Box p={3}>
        <Alert severity="error">
          {t("screens.operationsInbox.errors.loadFailed", { error: error.message })}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 3, pb: 12 }}>
      {/* Header */}
      <InboxHeader
        totalCount={totalCount}
        actionableCount={actionableCount}
        filters={filters}
        onFiltersChange={setFilters}
        items={items}
        lastRefreshTime={lastRefreshTime}
        isRefreshing={isLoading}
      />

      {/* Item Groups */}
      <Stack spacing={3} mt={4}>
        {/* New Tails Group (only in 'all' view) */}
        {filteredGroups.unknownAircraft.length > 0 && (
          <ActionTypeGroup
            title={t("screens.operationsInbox.groups.unknownAircraft.title")}
            count={filteredGroups.unknownAircraft.length}
            tooltip={t("screens.operationsInbox.groups.unknownAircraft.tooltip")}
            icon={<ErrorOutlineIcon fontSize="small" />}
            color="#ef4444"
            defaultExpanded={true}
            forceExpanded={!!filters.search}
            showSelectAll={filteredGroups.unknownAircraft.length > 0}
            allSelected={
              filteredGroups.unknownAircraft.length > 0 &&
              filteredGroups.unknownAircraft.every(i => selectedIds.has(i.item_id))
            }
            onSelectAll={() => {
              const ids = filteredGroups.unknownAircraft.map(i => i.item_id);
              if (filteredGroups.unknownAircraft.every(i => selectedIds.has(i.item_id))) {
                setSelectedIds(prev => {
                  const newSet = new Set(prev);
                  ids.forEach(id => newSet.delete(id));
                  return newSet;
                });
              } else {
                setSelectedIds(prev => new Set([...prev, ...ids]));
              }
            }}
          >
            {filteredGroups.unknownAircraft.map(item => (
              <QuarantineCard
                key={item.item_id}
                item={item}
                isSelected={selectedIds.has(item.item_id)}
                onToggleSelect={() => toggleSelection(item.item_id)}
                showCheckbox={true}
                onResolve={(aircraftId, aircraftName) =>
                  handleResolveAircraft(item, aircraftId, aircraftName)
                }
                onSnooze={(until) => handleSnooze(item, until)}
                isResolving={processingIds.has(item.item_id)}
              />
            ))}
          </ActionTypeGroup>
        )}

        {/* Assign Parking Group */}
        {filteredGroups.needsRouting.length > 0 && (
          <ActionTypeGroup
            title={t("screens.operationsInbox.groups.needsRouting.title")}
            count={filteredGroups.needsRouting.length}
            urgencyCounts={urgencyCounts}
            tooltip={t("screens.operationsInbox.groups.needsRouting.tooltip")}
            icon={<FlightIcon fontSize="small" />}
            color="#f59e0b"
            defaultExpanded={true}
            forceExpanded={!!filters.search}
            showSelectAll={filteredGroups.needsRouting.length > 0}
            allSelected={
              filteredGroups.needsRouting.length > 0 &&
              filteredGroups.needsRouting.every(i => selectedIds.has(i.item_id))
            }
            onSelectAll={() => {
              const ids = filteredGroups.needsRouting.map(i => i.item_id);
              if (filteredGroups.needsRouting.every(i => selectedIds.has(i.item_id))) {
                setSelectedIds(prev => {
                  const newSet = new Set(prev);
                  ids.forEach(id => newSet.delete(id));
                  return newSet;
                });
              } else {
                setSelectedIds(prev => new Set([...prev, ...ids]));
              }
            }}
          >
            {filteredGroups.needsRouting.map(item => (
              <TripCard
                key={item.item_id}
                item={item}
                hangars={hangars}
                ramps={ramps}
                locationUsageCounts={locationUsageCounts}
                onAssignToHangar={(hangarId, hangarName) =>
                  handleAssignToHangar(item, hangarId, hangarName)
                }
                onAssignToRamp={(rampId, rampName) =>
                  handleAssignToRamp(item, rampId, rampName)
                }
                onSnooze={(until) => handleSnooze(item, until)}
                isAssigning={processingIds.has(item.item_id)}
                isSelected={selectedIds.has(item.item_id)}
                onToggleSelect={() => toggleSelection(item.item_id)}
                showCheckbox={true}
              />
            ))}
          </ActionTypeGroup>
        )}

        {/* Snoozed Section */}
        {groups.snoozed.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: 'rgba(0, 0, 0, 0.02)',
              borderRadius: 2,
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={1}
              sx={{ cursor: 'pointer' }}
              onClick={() => setSnoozedExpanded(!snoozedExpanded)}
            >
              <SnoozeIcon fontSize="small" sx={{ color: 'text.secondary' }} />
              <Typography variant="body2" fontWeight={600} color="text.secondary">
                {t("screens.operationsInbox.groups.snoozed.count", { count: groups.snoozed.length })}
              </Typography>
              <Tooltip title={t("screens.operationsInbox.groups.snoozed.tooltip")} arrow placement="top">
                <HelpOutlineIcon
                  sx={{
                    fontSize: 14,
                    color: 'text.disabled',
                    cursor: 'help',
                    '&:hover': { color: 'text.secondary' },
                  }}
                  onClick={(e) => e.stopPropagation()}
                />
              </Tooltip>
              <IconButton size="small">
                {snoozedExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Box>
            <Collapse in={snoozedExpanded}>
              <Stack spacing={1} mt={2}>
                {groups.snoozed.map(item => (
                  <Box
                    key={item.item_id}
                    display="flex"
                    alignItems="center"
                    gap={2}
                    sx={{
                      p: 1,
                      bgcolor: 'background.paper',
                      borderRadius: 1,
                      opacity: 0.7,
                    }}
                  >
                    <Typography variant="body2" fontWeight={600} sx={{ width: 100 }}>
                      {item.tail_number}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ width: 180 }}>
                      {item.aircraft_type_hint || item.aircraft_make}
                    </Typography>
                    <Box flex={1} />
                    <Typography variant="caption" color="text.secondary">
                      {item.snoozed_until ? t("screens.operationsInbox.groups.snoozed.snoozedUntil", { date: new Date(item.snoozed_until).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }) }) : '—'}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={() => handleUnsnooze(item)}
                    >
                      <SnoozeIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
            </Collapse>
          </Paper>
        )}

        {/* Empty State - context-aware based on time filter */}
        {filteredCount === 0 && (
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              color: 'text.secondary',
            }}
          >
            {filters.timeFilter === 'today' ? (
              <>
                <Typography variant="h6" sx={{ mb: 1, color: 'success.main' }}>
                  {t("screens.operationsInbox.emptyStates.today.title")}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {t("screens.operationsInbox.emptyStates.today.message")}
                </Typography>
                {(groups.needsRouting.length > 0 || groups.unknownAircraft.length > 0) && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {groups.needsRouting.length > 0 && t("screens.operationsInbox.emptyStates.today.laterThisWeek", { count: groups.needsRouting.length })}
                      {groups.needsRouting.length > 0 && groups.unknownAircraft.length > 0 && ' · '}
                      {groups.unknownAircraft.length > 0 && t("screens.operationsInbox.emptyStates.today.newTailsToSetup", { count: groups.unknownAircraft.length })}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                      {groups.needsRouting.length > 0 && (
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setFilters(f => ({ ...f, timeFilter: 'week' }))}
                          sx={{ textTransform: 'none' }}
                        >
                          {t("screens.operationsInbox.emptyStates.today.previewThisWeek")}
                        </Button>
                      )}
                      {groups.unknownAircraft.length > 0 && (
                        <Button
                          variant="outlined"
                          size="small"
                          color="warning"
                          onClick={() => setFilters(f => ({ ...f, timeFilter: 'all' }))}
                          sx={{ textTransform: 'none' }}
                        >
                          {t("screens.operationsInbox.emptyStates.today.setupNewTails")}
                        </Button>
                      )}
                    </Box>
                  </Box>
                )}
              </>
            ) : filters.timeFilter === 'tomorrow' ? (
              <>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {t("screens.operationsInbox.emptyStates.tomorrow.title")}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {t("screens.operationsInbox.emptyStates.tomorrow.message")}
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setFilters(f => ({ ...f, timeFilter: 'all' }))}
                  sx={{ textTransform: 'none' }}
                >
                  {t("screens.operationsInbox.emptyStates.tomorrow.viewAll")}
                </Button>
              </>
            ) : filters.timeFilter === 'week' ? (
              <>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {t("screens.operationsInbox.emptyStates.week.title")}
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  {t("screens.operationsInbox.emptyStates.week.message")}
                </Typography>
                {groups.unknownAircraft.length > 0 && (
                  <Button
                    variant="outlined"
                    size="small"
                    color="warning"
                    onClick={() => setFilters(f => ({ ...f, timeFilter: 'all' }))}
                    sx={{ textTransform: 'none' }}
                  >
                    {t("screens.operationsInbox.emptyStates.week.setupNewTails", { count: groups.unknownAircraft.length })}
                  </Button>
                )}
              </>
            ) : (
              <>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  {t("screens.operationsInbox.emptyStates.all.title")}
                </Typography>
                <Typography variant="body2">
                  {t("screens.operationsInbox.emptyStates.all.message")}
                </Typography>
              </>
            )}
          </Box>
        )}
      </Stack>

      {/* Bulk Action Bar */}
      <BulkActionBar
        selectedItems={selectedItems}
        hangars={hangars}
        ramps={ramps}
        onBulkResolve={handleBulkResolve}
        onBulkAssignHangar={handleBulkAssignHangar}
        onBulkAssignRamp={handleBulkAssignRamp}
        onBulkSnooze={handleBulkSnooze}
        onClearSelection={clearSelection}
        isProcessing={bulkProcessing}
      />
    </Box>
  );
}

/**
 * Wrapper component that provides required context providers
 */
export default function OperationsInboxView() {
  return (
    <HangarsStateContainer isReference={false}>
      <RampsStateContainer isReference={false}>
        <OperationsInboxViewInner />
      </RampsStateContainer>
    </HangarsStateContainer>
  );
}
